import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { trackByRef } from 'src/app/lego-utils';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TaskListHeader } from 'src/app/models/domain';
import {
  DesignService,
  IPage,
  PagedCollection,
  TechnicalSpecificationService,
} from 'src/app/state';
import { TECHNICAL_SPECIFICATION_PAGINATOR } from './technical-specification-pagination.provider';

@Component({
  selector: 'em-technical-specification',
  templateUrl: './technical-specification.component.html',
  standalone: false,
})
export class TechnicalSpecificationComponent implements OnInit, OnDestroy {
  public pagination$?: Observable<IPage<TaskListHeader>>;
  public technicalSpecificationUiState$ = this.technicalSpecificationService.uiById$;

  public designLoading$ = this.designService.loading$;
  public technicalSpecificationsLoading$ = this.paginatorRef.loading$;
  public error$ = this.technicalSpecificationService.error$;
  public perPage = 10;
  public pageIndex = 1;
  public readonly trackByRef = trackByRef;

  private readonly showAll$ = new BehaviorSubject(false);
  private readonly perPage$ = new BehaviorSubject(10);
  private readonly subscriptions = new Subscription();

  /** See the matching note on ControlSpecificationComponent — the design is part of the key. */
  private readonly shouldClearCacheWhenChanged$ = combineLatest([
    this.showAll$,
    this.perPage$,
    this.designService.selectDesignId().pipe(distinctUntilChanged()),
  ]).pipe(
    tap(() => {
      this.paginatorRef.clearCache();
      this.paginatorRef.setPage(1);
    })
  );

  public constructor(
    private readonly designService: DesignService,
    private readonly technicalSpecificationService: TechnicalSpecificationService,

    @Inject(TECHNICAL_SPECIFICATION_PAGINATOR)
    public paginatorRef: PagedCollection<TaskListHeader>
  ) {}

  public ngOnInit() {
    this.pagination$ = combineLatest([
      this.paginatorRef.pageChanges,
      this.shouldClearCacheWhenChanged$,
    ]).pipe(
      switchMap(([page, [showAll, perPage]]) => {
        const req = showAll
          ? () => this.technicalSpecificationService.getAll()
          : () =>
              this.technicalSpecificationService.getPage({
                page,
                perPage,
              });

        return this.paginatorRef.getPage(req);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  public expansionChange(isExpanded: boolean, taskListHeader: TaskListHeader) {
    if (!isExpanded) {
      this.technicalSpecificationService.updateUiState(taskListHeader.id, {
        isExpanded,
        loading: false,
      });

      return;
    }

    this.subscriptions.add(
      this.technicalSpecificationService
        .populateOperationsByTaskListHeader(taskListHeader)
        .subscribe()
    );
  }

  public perPageChange(perPage: number) {
    this.perPage$.next(perPage);
  }

  public showAllSpecifications(showAll: boolean) {
    this.showAll$.next(showAll);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
